import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BiPlus, BiRefresh, BiX } from "react-icons/bi";
import AgentTable from "./AgentTable";
import BranchTable from "./BranchTable";
import CreateAgent from "./CreateAgent";
import CreateBranch from "./CreateBranch";
import ConfirmationModal from "./ConfirmationModal";
import LoadingSpinner from "./LoadingSpinner";

const Branch = () => {
  // State management
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState({
    agents: false,
    branches: false,
    loadingMoreBranches: false
  });
  const [modal, setModal] = useState({
    delete: false,
    createAgent: false,
    createBranch: false
  });
  const [selectedId, setSelectedId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [branchPage, setBranchPage] = useState(1);
  const [hasMoreBranches, setHasMoreBranches] = useState(true);
  const branchesPerPage = 10;

  // Data fetching with proper response handling
  const fetchData = useCallback(async (page = 1, isRefresh = false) => {
    try {
      if (isRefresh) {
        setLoading(prev => ({ ...prev, agents: true, branches: true }));
      } else {
        setLoading(prev => ({ ...prev, loadingMoreBranches: true }));
      }
      setError(null);

      const [agentsRes, branchesRes] = await Promise.all([
        axios.get('/api/user/branch'),
        axios.get(`/api/branch/branch?page=${page}&limit=${branchesPerPage}`)
      ]);

      // Handle agents response
      const agentsData = Array.isArray(agentsRes?.data) 
        ? agentsRes.data 
        : [];
      setAgents(agentsData);

      // Handle branches response
      const branchesData = branchesRes?.data?.branches || [];
      
      if (isRefresh) {
        setBranches(branchesData);
        setBranchPage(1);
      } else {
        setBranches(prev => {
          // Filter out duplicates before adding new branches
          const existingIds = prev.map(b => b._id);
          const newBranches = branchesData.filter(b => !existingIds.includes(b._id));
          return [...prev, ...newBranches];
        });
      }
      
      setHasMoreBranches(branchesData.length === branchesPerPage);

    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.response?.data?.message || "Error fetching data");
      if (isRefresh) {
        setAgents([]);
        setBranches([]);
      }
    } finally {
      if (isRefresh) {
        setLoading(prev => ({ ...prev, agents: false, branches: false }));
      } else {
        setLoading(prev => ({ ...prev, loadingMoreBranches: false }));
      }
    }
  }, [branchesPerPage]);

  // Initial load or refresh
  useEffect(() => {
    fetchData(1, true);
  }, [lastRefresh, fetchData]);

  const handleLoadMoreBranches = () => {
    const nextPage = branchPage + 1;
    setBranchPage(nextPage);
    fetchData(nextPage, false);
  };

  // Status toggle for branches
  const toggleBranchStatus = async (branchId, currentStatus) => {
    try {
      const updatedBranch = await axios.post(`/api/branch/updatebranch/${branchId}`, {
        status: !currentStatus
      });
      
      setBranches(prev => 
        prev.map(branch => 
          branch._id === branchId ? updatedBranch.data : branch
        )
      );
    } catch (err) {
      console.error("Status update error:", err);
      setError(err.response?.data?.message || "Failed to update branch status");
    }
  };

  // Status toggle for agents
  const toggleAgentStatus = async (agentId, currentStatus) => {
    try {
      const updatedAgent = await axios.post(`/api/user/updatebranch/${agentId}`, {
        status: !currentStatus
      });
      
      setAgents(prev => 
        prev.map(agent => 
          agent._id === agentId ? updatedAgent.data : agent
        )
      );
    } catch (err) {
      console.error("Status update error:", err);
      setError(err.response?.data?.message || "Failed to update agent status");
    }
  };

  // Delete handlers
  const handleDeletePrompt = (id) => {
    setSelectedId(id);
    setModal(prev => ({ ...prev, delete: true }));
  };

  const handleDeleteAgent = async () => {
    try {
      await axios.delete(`/api/user/deletebranch/${selectedId}`);
      setAgents(prev => prev.filter(agent => agent._id !== selectedId));
      closeModal();
    } catch (err) {
      setError("Failed to delete agent");
    }
  };

  const handleDeleteBranch = async (branchId) => {
    try {
      await axios.delete(`/api/branch/deletebranch/${branchId}`);
      setBranches(prev => prev.filter(branch => branch._id !== branchId));
    } catch (err) {
      setError("Failed to delete branch");
    }
  };

  // Modal controls
  const openCreateAgentModal = () => setModal(prev => ({ ...prev, createAgent: true }));
  const openCreateBranchModal = () => setModal(prev => ({ ...prev, createBranch: true }));
  const closeModal = () => setModal({ delete: false, createAgent: false, createBranch: false });

  // Refresh data
  const handleRefresh = () => {
    setLastRefresh(Date.now());
  };

  // Success handlers
  const handleCreateSuccess = () => {
    closeModal();
    handleRefresh();
  };

  return (
    <div className="tw-container tw-mx-auto tw-p-4 tw-space-y-8 tw-relative">
      {/* Agents Section */}
      <section className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-800">Agents</h2>
          <div className="tw-flex tw-gap-4">
            <button
              onClick={handleRefresh}
              disabled={loading.agents || loading.branches}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
            >
              <BiRefresh className={`tw-text-lg ${loading.agents ? "tw-animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={openCreateAgentModal}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
            >
              <BiPlus className="tw-text-lg" />
              Add Agent
            </button>
          </div>
        </div>

        {loading.agents ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="tw-text-red-500 tw-p-4">{error}</div>
        ) : (
          <AgentTable 
            agents={agents} 
            onToggleStatus={toggleAgentStatus}
            onDelete={handleDeletePrompt}
          />
        )}
      </section>

      {/* Branches Section */}
      <section className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-800">
            Branches
            {!loading.branches && (
              <span className="tw-text-sm tw-font-normal tw-ml-2">
                ({branches.length} {hasMoreBranches ? "+" : ""} branches)
              </span>
            )}
          </h2>
          <div className="tw-flex tw-gap-4">
            <button
              onClick={handleRefresh}
              disabled={loading.branches}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
            >
              <BiRefresh className={`tw-text-lg ${loading.branches ? "tw-animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={openCreateBranchModal}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
            >
              <BiPlus className="tw-text-lg" />
              Add Branch
            </button>
          </div>
        </div>

        {loading.branches ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="tw-text-red-500 tw-p-4">{error}</div>
        ) : branches.length === 0 ? (
          <div className="tw-text-gray-500 tw-p-4">No branches found</div>
        ) : (
          <>
            <BranchTable 
              branches={branches} 
              onToggleStatus={toggleBranchStatus}
              onDelete={handleDeleteBranch}
            />
            
            {hasMoreBranches && (
              <div className="tw-mt-4 tw-text-center">
                <button
                  onClick={handleLoadMoreBranches}
                  disabled={loading.loadingMoreBranches}
                  className="tw-px-6 tw-py-2 tw-bg-blue-100 tw-text-blue-800 tw-rounded-md hover:tw-bg-blue-200 tw-transition-colors"
                >
                  {loading.loadingMoreBranches ? (
                    <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                      <LoadingSpinner size="small" />
                      Loading More Branches...
                    </span>
                  ) : (
                    "Load More Branches"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Modal Overlays */}
      {(modal.createAgent || modal.createBranch || modal.delete) && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
          {/* Create Agent Modal */}
          {modal.createAgent && (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-max-w-md tw-w-full tw-max-h-[90vh] tw-overflow-y-auto tw-relative">
              <button 
                onClick={closeModal}
                className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-500 hover:tw-text-gray-700"
              >
                <BiX className="tw-text-2xl" />
              </button>
              <CreateAgent 
                onSuccess={handleCreateSuccess} 
                onClose={closeModal}
              />
            </div>
          )}

          {/* Create Branch Modal */}
          {modal.createBranch && (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-max-w-md tw-w-full tw-max-h-[90vh] tw-overflow-y-auto tw-relative">
              <button 
                onClick={closeModal}
                className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-500 hover:tw-text-gray-700"
              >
                <BiX className="tw-text-2xl" />
              </button>
              <CreateBranch 
                onSuccess={handleCreateSuccess} 
                onClose={closeModal}
              />
            </div>
          )}

          {/* Confirmation Modal */}
          {modal.delete && (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-max-w-md tw-w-full">
              <ConfirmationModal
                title="Confirm Deletion"
                message="Are you sure you want to delete this agent?"
                onConfirm={handleDeleteAgent}
                onCancel={closeModal}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Branch;